import * as Sentry from '@sentry/nextjs'

/**
 * RPC error codes set via ERRCODE in our Postgres functions.
 * Class scheme starts in supabase/migrations/20260309110000_add_rpc_error_codes.sql.
 * Consultation reasons that need different user-facing copy have their own codes
 * (20260922120000_consultation_guard_reason_codes.sql). CL403 stays the generic
 * forbidden bucket. Do not tell those reasons apart by exception text.
 */
export const RPC_ERROR_CODES = {
  INSUFFICIENT_STOCK: 'CL001',
  VALIDATION: 'CL002',
  NOT_FOUND: 'CL003',
  CONFLICT: 'CL004',
  DELETE_BLOCKED: 'CL005',
  UNAUTHORIZED: 'CL401',
  FORBIDDEN: 'CL403',
  /** Refraction completion requires consultation.complete or consultation.save_full. */
  CONSULTATION_COMPLETE_REFRACTION_DENIED: 'CL410',
  /** Non-refraction completion requires consultation.complete. */
  CONSULTATION_COMPLETE_ROLE_DENIED: 'CL411',
  /** Visit cannot be completed because clinician_id is null. */
  CONSULTATION_NO_TREATING_CLINICIAN: 'CL412',
  /** Caller is not the visit's treating clinician. */
  CONSULTATION_ASSIGNED_CLINICIAN_REQUIRED: 'CL414',
  /** Prescription includes a restricted drug and the caller lacks prescribe_restricted. */
  PRESCRIBE_RESTRICTED_DENIED: 'CL415',
  /**
   * Draft cache optimistic-concurrency failure surfaced as an error.
   * The live upsert returns {status:'conflict'} instead of raising; this code is for
   * the error-shaped path.
   */
  CONSULTATION_DRAFT_CONFLICT: 'CL416',
  /** Draft cache row disappeared between read and write. */
  CONSULTATION_DRAFT_EXPIRED: 'CL417',
  /** Unique violation on consultations.visit_id, translated from CONSTRAINT_NAME in SQL. */
  CONSULTATION_VISIT_ALREADY_EXISTS: 'CL418',
  /** Completed consultation is past its 24-hour edit window. */
  CONSULTATION_EDIT_WINDOW_CLOSED: 'CL419',
  /** Only the treating clinician can edit a completed consultation. */
  CONSULTATION_EDIT_CLINICIAN_REQUIRED: 'CL420',
  /** Completed consultations can only be re-saved as finalized, not as drafts. */
  CONSULTATION_COMPLETED_DRAFT_DENIED: 'CL421',
} as const

/**
 * Error codes representing expected business outcomes — auto-skipped by reportError.
 * Forbidden and consultation-workflow denials are expected guard results; surfacing
 * them as Sentry errors drowns signal (same intent as UserFacingError on the client).
 */
const EXPECTED_RPC_CODES: ReadonlySet<string> = new Set([
  RPC_ERROR_CODES.INSUFFICIENT_STOCK,
  RPC_ERROR_CODES.CONFLICT,
  RPC_ERROR_CODES.DELETE_BLOCKED,
  RPC_ERROR_CODES.FORBIDDEN,
  RPC_ERROR_CODES.CONSULTATION_COMPLETE_REFRACTION_DENIED,
  RPC_ERROR_CODES.CONSULTATION_COMPLETE_ROLE_DENIED,
  RPC_ERROR_CODES.CONSULTATION_NO_TREATING_CLINICIAN,
  RPC_ERROR_CODES.CONSULTATION_ASSIGNED_CLINICIAN_REQUIRED,
  RPC_ERROR_CODES.PRESCRIBE_RESTRICTED_DENIED,
  RPC_ERROR_CODES.CONSULTATION_DRAFT_CONFLICT,
  RPC_ERROR_CODES.CONSULTATION_DRAFT_EXPIRED,
  RPC_ERROR_CODES.CONSULTATION_VISIT_ALREADY_EXISTS,
  RPC_ERROR_CODES.CONSULTATION_EDIT_WINDOW_CLOSED,
  RPC_ERROR_CODES.CONSULTATION_EDIT_CLINICIAN_REQUIRED,
  RPC_ERROR_CODES.CONSULTATION_COMPLETED_DRAFT_DENIED,
])

/**
 * Client-side guard / validation shown to the user as a toast — not an application defect.
 * Prevents noisy Sentry events when the UI and mutation both enforce the same rules.
 */
export class UserFacingError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UserFacingError'
  }
}

type ErrorKind = 'error' | 'postgrest' | 'message' | 'unknown'

interface PostgrestLikeError {
  code?: unknown
  details?: unknown
  hint?: unknown
  message: string
  name?: unknown
}

export interface NormalizedError {
  code?: string
  details?: string
  hint?: string
  kind: ErrorKind
  message: string
  name: string
}

export interface ReportErrorOptions {
  fallbackMessage?: string
  context?: Record<string, unknown>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function toOptionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function resolveMessage(value: string | undefined, fallbackMessage: string): string {
  return toOptionalString(value) ?? fallbackMessage
}

function isPostgrestLikeError(error: unknown): error is PostgrestLikeError {
  return (
    isRecord(error) &&
    typeof error.message === 'string' &&
    ('code' in error || 'details' in error || 'hint' in error)
  )
}

function normalizeContext(
  context: Record<string, unknown>
): Record<string, string | number | boolean | null> {
  return Object.fromEntries(
    Object.entries(context).map(([key, value]) => {
      if (value === null || value === undefined) return [key, null]
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return [key, value]
      }
      if (value instanceof Error) {
        return [key, value.message]
      }
      return [key, JSON.stringify(value)]
    })
  )
}

function createErrorFromNormalized(error: NormalizedError): Error {
  const normalized = new Error(error.message)
  normalized.name = error.name
  return normalized
}

function normalizePostgrestBranch(
  error: PostgrestLikeError,
  fallbackMessage: string
): NormalizedError {
  return {
    kind: 'postgrest',
    message: resolveMessage(error.message, fallbackMessage),
    name: toOptionalString(error.name) ?? 'PostgrestError',
    code: toOptionalString(error.code),
    details: toOptionalString(error.details),
    hint: toOptionalString(error.hint),
  }
}

export function normalizeError(
  error: unknown,
  fallbackMessage = 'An unexpected error occurred'
): NormalizedError {
  // PostgREST errors extend Error; the generic Error branch would drop SQLSTATE / details / hint.
  if (error instanceof Error && error.name === 'PostgrestError' && isPostgrestLikeError(error)) {
    return normalizePostgrestBranch(error, fallbackMessage)
  }

  if (error instanceof Error) {
    return {
      kind: 'error',
      message: resolveMessage(error.message, fallbackMessage),
      name: error.name || 'Error',
    }
  }

  if (isPostgrestLikeError(error)) {
    return normalizePostgrestBranch(error, fallbackMessage)
  }

  if (typeof error === 'string') {
    return {
      kind: 'message',
      message: resolveMessage(error, fallbackMessage),
      name: 'StringError',
    }
  }

  if (isRecord(error) && typeof error.message === 'string') {
    return {
      kind: 'message',
      message: resolveMessage(error.message, fallbackMessage),
      name: toOptionalString(error.name) ?? 'NonErrorThrowable',
    }
  }

  return {
    kind: 'unknown',
    message: fallbackMessage,
    name: 'UnknownError',
  }
}

export function getErrorMessage(
  error: unknown,
  fallbackMessage = 'An unexpected error occurred'
): string {
  return normalizeError(error, fallbackMessage).message
}

const INTERNAL_STORAGE_MESSAGE =
  /invalid key|object name contains invalid characters|the object name is invalid/i

function isStorageApiError(error: unknown): boolean {
  if (error instanceof Error && error.name === 'StorageApiError') return true
  return isRecord(error) && toOptionalString(error.name) === 'StorageApiError'
}

function storageApiUserFacingMessage(error: unknown): string {
  const text = [getErrorMessage(error, ''), collectInfrastructureErrorText(error)].join(' ')
  const status = isRecord(error) && typeof error.status === 'number' ? error.status : undefined
  const statusCode = isRecord(error) ? toOptionalString(error.statusCode) : undefined

  if (
    status === 403 ||
    statusCode === '403' ||
    /not allowed|unauthorized|forbidden|row-level security/i.test(text)
  ) {
    return 'You do not have permission to upload this file.'
  }
  if (status === 413 || /too large|payload too large|maximum allowed size/i.test(text)) {
    return 'This file is too large to upload.'
  }
  if (/already exists|duplicate|resource already/i.test(text)) {
    return 'A file with this name already exists. Try again or choose a different file.'
  }
  return 'This file could not be uploaded. Please try again.'
}

/**
 * Copy safe to show in toasts. Storage/API internals (object keys, status
 * strings) never pass through; UserFacingError and other staff-authored
 * messages do.
 */
export function getUserFacingErrorMessage(
  error: unknown,
  fallbackMessage = 'Please try again.'
): string {
  if (error instanceof UserFacingError) {
    return resolveMessage(error.message, fallbackMessage)
  }

  if (isStorageApiError(error) || INTERNAL_STORAGE_MESSAGE.test(getErrorMessage(error, ''))) {
    return storageApiUserFacingMessage(error)
  }

  if (error instanceof Error) {
    return resolveMessage(error.message, fallbackMessage)
  }

  const normalized = normalizeError(error, fallbackMessage)
  if (INTERNAL_STORAGE_MESSAGE.test(normalized.message)) {
    return storageApiUserFacingMessage(error)
  }
  return normalized.message
}

/** User-visible query/network failures from Supabase — includes PostgREST details/hint when helpful. */
export function getSupabaseQueryErrorMessage(
  error: unknown,
  fallbackMessage = 'An unexpected error occurred'
): string {
  const normalized = normalizeError(error, fallbackMessage)
  const extras: string[] = []
  if (normalized.details && !normalized.message.includes(normalized.details)) {
    extras.push(normalized.details)
  }
  if (normalized.hint && !normalized.message.includes(normalized.hint)) {
    extras.push(normalized.hint)
  }
  if (extras.length === 0) return normalized.message
  return `${normalized.message} (${extras.join(' — ')})`
}

/** Returns true when the error carries an expected business-outcome RPC code. */
export function isExpectedRpcError(error: unknown): boolean {
  const normalized = normalizeError(error)
  return !!normalized.code && EXPECTED_RPC_CODES.has(normalized.code)
}

/** PostgREST row-level security denial (often transient while auth/profile hydrates). */
export function isPostgrestRlsDenied(error: unknown): boolean {
  const normalized = normalizeError(error)
  if (normalized.code !== '42501') return false
  const text = [normalized.message, normalized.details, normalized.hint].filter(Boolean).join(' ')
  return /row-level security policy/i.test(text)
}

/** Postgres data coercion failures on json/jsonb inputs — usually fixable by correcting form data. */
export function isPostgrestInvalidJson(error: unknown): boolean {
  const normalized = normalizeError(error)
  return normalized.code === '22P02' || /invalid input syntax for type json/i.test(normalized.message)
}

/** Concurrent consultation draft autosave — another editor saved first. */
export function isConsultationDraftCacheConflict(error: unknown): boolean {
  return normalizeError(error).code === RPC_ERROR_CODES.CONSULTATION_DRAFT_CONFLICT
}

/** Draft cache row was removed or expired before this write landed. */
export function isConsultationDraftCacheExpired(error: unknown): boolean {
  return normalizeError(error).code === RPC_ERROR_CODES.CONSULTATION_DRAFT_EXPIRED
}

/** Duplicate consultation row for the same visit. SQL translates the unique violation to CL418. */
export function isConsultationVisitIdConflict(error: unknown): boolean {
  return normalizeError(error).code === RPC_ERROR_CODES.CONSULTATION_VISIT_ALREADY_EXISTS
}

const TRANSIENT_INFRASTRUCTURE_PATTERN =
  /(failed to fetch|fetch failed|network error|load failed|network request failed|err_internet_disconnected|offline|connection reset|aborterror|abort error|request was aborted|operation was aborted|lock broken|navigator lockmanager|steal option|timed out waiting for auth|timeout exceeded|signal timed out|the operation timed out)/i

function collectInfrastructureErrorText(error: unknown, depth = 0): string {
  if (depth > 4 || error === null || error === undefined) return ''

  const normalized = normalizeError(error, '')
  const parts = [normalized.message, normalized.details, normalized.hint].filter(Boolean)

  if (isRecord(error)) {
    for (const key of ['error', 'cause', 'details'] as const) {
      if (key in error) {
        parts.push(collectInfrastructureErrorText(error[key], depth + 1))
      }
    }
  }

  return parts.join(' ')
}

/**
 * Client-side connectivity / auth-lock contention — not an application defect.
 * Used to avoid noisy Sentry events on slow or contested mobile browsers.
 */
export function isTransientInfrastructureError(error: unknown): boolean {
  if (
    isRecord(error) &&
    'isAcquireTimeout' in error &&
    error.isAcquireTimeout === true
  ) {
    return true
  }

  const text = collectInfrastructureErrorText(error)
  return text.length > 0 && TRANSIENT_INFRASTRUCTURE_PATTERN.test(text)
}

/** @deprecated Prefer isTransientInfrastructureError — kept for consultation hooks. */
export function isLikelyNetworkError(error: unknown): boolean {
  return isTransientInfrastructureError(error)
}

export function reportError(
  error: unknown,
  options: ReportErrorOptions = {}
): NormalizedError {
  const normalized = normalizeError(error, options.fallbackMessage)

  // Auto-skip expected business errors identified by RPC error code
  if (normalized.code && EXPECTED_RPC_CODES.has(normalized.code)) return normalized

  if (isPostgrestRlsDenied(error)) return normalized

  if (isPostgrestInvalidJson(error)) return normalized

  if (isConsultationVisitIdConflict(error)) return normalized

  if (isConsultationDraftCacheConflict(error)) return normalized

  if (isTransientInfrastructureError(error)) return normalized

  if (error instanceof UserFacingError) return normalized

  const throwable = error instanceof Error ? error : createErrorFromNormalized(normalized)

  Sentry.withScope((scope) => {
    scope.setTag('error_kind', normalized.kind)

    if (normalized.code) {
      scope.setTag('error_code', normalized.code)
    }

    scope.setContext('normalized_error', {
      code: normalized.code ?? null,
      details: normalized.details ?? null,
      hint: normalized.hint ?? null,
      kind: normalized.kind,
      message: normalized.message,
      name: normalized.name,
    })

    if (options.context) {
      scope.setContext('error_context', normalizeContext(options.context))
    }

    Sentry.captureException(throwable)
  })

  return normalized
}
